import React from 'react'
import { observer } from 'mobx-react-lite'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardHeaderActions,
} from 'components/Card'
import { CapturedInformationTab as Tab } from '../DetailsView/DetailsViewModel'
import styles from './CapturedInformationCard.sass'
import { QuestionDTO } from 'jobs/dtos/questions'
import { QuestionViewModelFor } from 'jobs/questions/type-registry'
import ResponseValue from 'jobs/components/ResponseValue'
import { TabBar, TabItem } from 'components/TabBar'
import { match } from 'utils/pattern-match-util'
import { JobCardEmptyState } from '../JobCardEmptyState'
import VariableListViewModel from 'jobs/variables/VariableListViewModel'
import QuestionListViewModel from 'jobs/questions/QuestionListViewModel'
import JobViewModel from '../JobViewModel'
import { SectionError, SectionLoading } from '../Section/Section'
import Icon from 'components/Icon'
import Tooltip from 'components/Tooltip'
import { IconButton } from 'components/IconButton'

interface CapturedInformationProps {
  vm: JobViewModel
}

export default observer(function CapturedInformationCard({
  vm,
}: CapturedInformationProps) {
  React.useEffect(() => {
    // Always show the card by default when the component is first mounted
    vm.detailsVM.responsesSection.set(true)
    vm.detailsVM.variablesTab.set(true)
  }, [vm.detailsVM.responsesSection, vm.detailsVM.variablesTab])

  return (
    <Card>
      <CardHeader compact bordered>
        <CardTitle cardLayout>
          Captured information
          {vm.showDraftView && (
            <CardHeaderActions>
              <Tooltip content="Edit responses">
                <IconButton
                  data-test="captured-information-card-edit-responses-button"
                  onClick={vm.editDraft}
                >
                  <Icon name="edit-outlined" size={20} />
                </IconButton>
              </Tooltip>
            </CardHeaderActions>
          )}
        </CardTitle>
        <div className={styles.tabs}>
          <TabBar>
            <TabItem
              cardLayout
              active={
                vm.detailsVM.capturedInformationTabController.current.tab ===
                Tab.Questions
              }
              onClick={() =>
                vm.detailsVM.capturedInformationTabController.change(
                  Tab.Questions
                )
              }
              dataTest="questions-tab-item"
            >
              Questions
            </TabItem>
            <TabItem
              cardLayout
              active={
                vm.detailsVM.capturedInformationTabController.current.tab ===
                Tab.Variables
              }
              onClick={() =>
                vm.detailsVM.capturedInformationTabController.change(
                  Tab.Variables
                )
              }
              dataTest="variables-tab-item"
            >
              Variables
            </TabItem>
          </TabBar>
        </div>
      </CardHeader>
      <CardContent scroll>
        {match(vm.detailsVM.capturedInformationTabController.current.tab, {
          [Tab.Questions]: () =>
            vm.detailsVM.fetchResponses.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading questions." error={err} />
              ),
              resolved: () =>
                vm.detailsVM.responses.questions.length > 0 ||
                vm.job.answers.length > 0 ? (
                  <QuestionsTab vm={vm.detailsVM.responses} />
                ) : (
                  <JobCardEmptyState
                    topPadding
                    helpText="Nothing to display."
                  />
                ),
            }),
          [Tab.Variables]: () =>
            vm.detailsVM.fetchResponses.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading questions." error={err} />
              ),
              resolved: () =>
                vm.detailsVM.variablesVM.variables.length > 0 ? (
                  <VariablesTab vm={vm.detailsVM.variablesVM} />
                ) : (
                  <JobCardEmptyState
                    topPadding
                    helpText="Legend Variables allow you to compute useful information based on Questions.
              When a Job is using a Legend that utilizes Variables, they will show up here."
                  />
                ),
            }),
        })}
      </CardContent>
    </Card>
  )
})

interface QuestionItemProps<T extends QuestionDTO> {
  questionVM: QuestionViewModelFor<T>
}

function Question<T extends QuestionDTO>({ questionVM }: QuestionItemProps<T>) {
  return (
    <div className={styles.capturedInfoItem}>
      <div className={styles.capturedInfoLabel}>{questionVM.question}</div>
      <div className={styles.capturedInfoValue}>
        <ResponseValue vm={questionVM} />
      </div>
    </div>
  )
}

interface QuestionTabProps {
  vm: QuestionListViewModel
}

export const QuestionsTab = ({ vm }: QuestionTabProps) => {
  return (
    <div>
      {vm.questions.map((vm) => (
        <Question key={vm.id} questionVM={vm} />
      ))}
    </div>
  )
}

interface VariableTabProps {
  vm: VariableListViewModel
}

export const VariablesTab = ({ vm }: VariableTabProps) => {
  return (
    <>
      {vm.variables.map((vm) => {
        return (
          <div key={vm.id} className={styles.capturedInfoItem}>
            <div className={styles.capturedInfoLabel}>{vm.id}</div>
            <div className={styles.capturedInfoValue}>{vm.textValue}</div>
          </div>
        )
      })}
    </>
  )
}
